import { FunctionComponent, useState } from 'react';
import { Button, Col, Image, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import { CloseOutlined } from '@ant-design/icons';
import { getOrInitCheckoutSession } from '@monorepo/client-common';
import { useResponsiveness } from '@monorepo/react-hooks';
import {
  Banner,
  BannerImages,
  BannerPlacement,
  CheckoutEntryPath,
  CheckoutSource,
} from '@monorepo/types';
import { usePromotionalBannersState } from '../../features/account-dashboard/components/sections/promotional-banners/hooks';
import { ERROR_TYPES, UIErrorService } from '../../services/core/error-service';

interface Props {
  banners?: Banner[];
  onClose?: VoidFunction;
}

const HeaderBanner: FunctionComponent<Props> = ({ banners = [], onClose }) => {
  const { isMobile, isTablet } = useResponsiveness();
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const banner = banners.find(
    (banner) => banner.placement === BannerPlacement.Header,
  );

  const { handleBannerClick, isCartLoading } = usePromotionalBannersState({
    bannerName: banner?.name || '',
    bannerId: banner?.bannerId,
    link: banner?.link,
    beforeNavigate: () => {
      if (banner?.link) {
        const isCheckoutBanner = banner.link.includes('/purchase');
        if (isCheckoutBanner && !banner.isCheckout) {
          getOrInitCheckoutSession(
            CheckoutEntryPath.NewOrder,
            CheckoutSource.WelcomeBanner,
          );
        }
      }
    },
  });

  if (!isBannerVisible || !banner) {
    return null;
  }

  const getBannerUrl = (images: BannerImages) => {
    if (isMobile && images.mobileUrl) {
      return images.mobileUrl;
    }

    if (isTablet && images.tabletUrl) {
      return images.tabletUrl;
    }

    return images.desktopUrl;
  };

  const bannerUrl = getBannerUrl(banner.images);

  if (!bannerUrl) {
    new UIErrorService({
      name: ERROR_TYPES.CLIENT,
      message: `Banner URL is not defined for banner: ${banner.name}`,
      data: {
        bannerName: banner.name,
        bannerId: banner.bannerId,
        bannerImages: banner.images,
      },
    });
    return null;
  }

  const hideBanner = () => {
    setIsBannerVisible(false);
    onClose?.();
  };

  return (
    <Row justify={'center'} className={'header-banner'}>
      <Col span={24}>
        <button
          type="button"
          onClick={handleBannerClick}
          disabled={isCartLoading}
          style={{
            border: 'none',
            padding: 0,
            background: 'none',
            width: '100%',
            display: 'block',
            cursor: 'pointer',
          }}
          aria-label={banner.name}
          data-is-checkout={banner.isCheckout}
          data-banner-id={banner.bannerId || banner.name}
        >
          <Image
            width="100%"
            src={bannerUrl}
            preview={false}
            alt={banner.name}
          />
        </button>
        <Button
          type={'text'}
          className={'close-btn'}
          icon={<CloseOutlined />}
          onClick={hideBanner}
        />
      </Col>
    </Row>
  );
};

export default observer(HeaderBanner);
